import React, { useMemo, useRef, useState } from "react";
import { FlaskConical, ChevronLeft, SendHorizonal, Loader2, X, Hash } from "lucide-react";
import { extractPlaceholders, placeholderLabel } from "./features/template.placeholders";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TestTemplateDialog = ({ template, onClose, onSend }) => {
  const overlayRef = useRef(null);
  const placeholders = useMemo(() => extractPlaceholders(template), [template]);

  const [step, setStep] = useState(1);
  const [dataPoints, setDataPoints] = useState(() =>
    Object.fromEntries(placeholders.map((k) => [k, ""]))
  );
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  if (!template) return null;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current && !sending) onClose();
  };

  const handleNext = () => setStep(2);
  const handleBack = () => setStep(1);

  const handleSend = async () => {
    if (!email.trim() || !emailRegex.test(email.trim())) return;
    setSending(true);
    await onSend({ data_points: dataPoints, test_email: email.trim() });
    setSending(false);
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
    >
      <div className="bg-surface border border-border rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">

        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-border bg-success_bg">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-success/10 border border-success_border flex-shrink-0">
            <FlaskConical size={18} className="text-success" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-text_primary">Test Template</h3>
            <div className="flex items-center gap-1 text-xs text-text_muted mt-0.5">
              <Hash size={11} />
              <span className="font-mono truncate">{template.template_key}</span>
            </div>
          </div>
          <button onClick={onClose} disabled={sending} className="text-text_muted disabled:opacity-50 flex-shrink-0">
            <X size={18} />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 px-6 py-3 border-b border-border bg-background">
          <div className={`flex items-center gap-1.5 text-xs font-medium ${step === 1 ? "text-primary" : "text-text_muted"}`}>
            <span className={`flex items-center justify-center w-5 h-5 rounded-full text-[11px] font-semibold border ${step === 1 ? "bg-primary text-white border-primary" : "bg-surface_alt border-border"}`}>1</span>
            Data Points
          </div>
          <div className="flex-1 h-px bg-border" />
          <div className={`flex items-center gap-1.5 text-xs font-medium ${step === 2 ? "text-primary" : "text-text_muted"}`}>
            <span className={`flex items-center justify-center w-5 h-5 rounded-full text-[11px] font-semibold border ${step === 2 ? "bg-primary text-white border-primary" : "bg-surface_alt border-border"}`}>2</span>
            Recipient
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 max-h-[55vh] overflow-y-auto">
          {step === 1 ? (
            placeholders.length ? (
              placeholders.map((key) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-text_secondary mb-1.5">
                    {placeholderLabel(key)}{" "}
                    <span className="font-mono text-[11px] text-text_muted">{`{{${key}}}`}</span>
                  </label>
                  <input
                    type="text"
                    value={dataPoints[key] ?? ""}
                    onChange={(e) => setDataPoints((p) => ({ ...p, [key]: e.target.value }))}
                    placeholder={`Enter ${placeholderLabel(key)}`}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-surface_alt text-text_primary placeholder:text-text_muted focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                  />
                </div>
              ))
            ) : (
              <p className="text-xs text-text_muted italic">
                This template has no {"{{placeholder}}"} data points — you can proceed directly to the recipient step.
              </p>
            )
          ) : (
            <div>
              <label className="block text-xs font-medium text-text_secondary mb-1.5">
                Recipient Email <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !sending) handleSend(); }}
                placeholder="you@example.com"
                disabled={sending}
                className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-surface_alt text-text_primary placeholder:text-text_muted focus:outline-none focus:ring-2 focus:ring-success/30 focus:border-success disabled:opacity-60 transition"
              />
              <p className="text-[11px] text-text_muted mt-1.5">
                The template will be rendered with your data points and sent here for validation.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-border bg-surface_alt">
          {step === 2 ? (
            <button
              onClick={handleBack}
              disabled={sending}
              className="inline-flex items-center gap-1 px-4 py-2 text-xs font-semibold rounded-lg bg-surface text-text_secondary border border-border disabled:opacity-50"
            >
              <ChevronLeft size={13} /> Back
            </button>
          ) : (
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold rounded-lg bg-surface text-text_secondary border border-border"
            >
              Cancel
            </button>
          )}

          {step === 1 ? (
            <button
              onClick={handleNext}
              className="px-4 py-2 text-xs font-semibold rounded-lg bg-primary text-white shadow-sm"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSend}
              disabled={sending || !email.trim() || !emailRegex.test(email.trim())}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg bg-success text-white disabled:opacity-60 shadow-sm"
            >
              {sending ? (
                <><Loader2 size={13} className="animate-spin" /> Sending…</>
              ) : (
                <><SendHorizonal size={13} /> Send Test</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestTemplateDialog;
